/**
 * Coding review fixture — Longest Increasing Subsequence seed (spec §8.5).
 *
 * The shared Review tab data model (`ReviewComment`/`ReviewSubmission` and
 * friends, spec §8.2/§8.4) used to be declared here. It now lives in
 * `review/contracts.ts`, where the live slice can depend on it without
 * importing `_mock-data` (LG-503) — this file is the fixture values it is
 * named for, and nothing else.
 */

import type {
  PracticeSessionRow,
  ReviewApproach,
  ReviewPlanWeek,
  SubmittedReviewRow,
} from '../../review/contracts'

export const MOCK_CODING_REVIEW: SubmittedReviewRow = {
  id: 'coding-lis',
  type: 'coding',
  title: 'Longest Increasing Subsequence',
  chips: ['Python', 'Beats 94%'],
  submittedWhen: 'Submitted 1 day ago',
  sprint: 'Sprint 13',
  // Worked on the Tuesday of the seed week — the tab files a row under the
  // week the work happened in, never the week its card was set for (ADR 0013).
  workedAtMs: Date.parse('2026-08-11T09:00:00.000Z'),
  // The LIS listing the Code section used to hardcode for every client
  // (LG-517). It is fixture data, so it lives with the fixtures: the live
  // path reads the client's own code off the submission artifact, and the
  // mock lane keeps a submission worth demoing. `testSummary` stays null —
  // the seed's old '6/6 passed' tally was invented and LG-632 cut it; the
  // line renders only where an artifact records a real execution result.
  code: {
    language: 'Python',
    lines: [
      'def lengthOfLIS(nums):',
      '    tails = []',
      '    for x in nums:',
      '        i = bisect_left(tails, x)',
      '        if i == len(tails):',
      '            tails.append(x)',
      '        else:',
      '            tails[i] = x',
      '    return len(tails)',
    ],
    testSummary: null,
  },
  ranking: {
    position: 1,
    reason:
      'Ranked first — signs of a real struggle despite the strong final result:',
    // No run-history chip (LG-632): run counts, run targets and what each
    // run re-ran are stored nowhere — the same invented facts the
    // Verification section was deleted over. The two chips below restate
    // only what the seed's own post-mortem holds.
    chips: [
      {
        icon: '😤',
        label: 'Frustration in reflection',
        tone: 'red',
        detail:
          '"Spent ~6 minutes debugging before spotting it" — tone analysis flags mounting frustration in the improvement note.',
      },
      {
        icon: '?',
        label: 'Asked a question',
        tone: 'blue',
        detail:
          'Post-mortem asks whether bisect_right is ever correct here — waiting on your answer.',
      },
    ],
  },
  // Coding pacing benchmarks and note cut (LG-632, spec D17) — the seed
  // carries exactly what the live mapper carries: the total and nothing
  // else. The old 'incl. 1 wrong submit' note is gone with them: no stored
  // field backs it, and the coding card renders only TOTAL + duration.
  pacing: {
    total: '28:14',
    totalNote: '',
    companyRows: [],
  },
  // The client's own claim (LG-639, spec D19), not the section LG-632 deleted:
  // the seed carries what the live mapper carries — two values recorded on the
  // post-mortem form, each its own comment target.
  complexity: {
    time: {
      value: 'O(n log n)',
      explanation: 'where n is the length of nums — one bisect per element.',
    },
    space: { value: 'O(n)', explanation: null },
  },
  // What the client did on the way here (LG-637), matching the story their own
  // post-mortem below tells: a first submit that failed on duplicates, then a
  // pass. Fixture values, and only the mock lane ever sees them — the live
  // path builds this in `toReviewSubmission` from the artifact's own `counts`
  // and `submits`, so a number invented here cannot reach a real review.
  //
  // Targets are set on the seed so the mock shell demos the judged state. Live
  // engagements have none (spec D18), which is why the section is written to
  // render the counts bare and is tested that way.
  verification: {
    runs: { value: 5, target: 3 },
    submits: { value: 2, target: 2 },
    failedSubmits: [
      {
        executionId: 'exec-lis-1',
        attempt: 1,
        passed: 4,
        total: 6,
        // 0-based, as `TestResult.index` is stored — the section renders these
        // as Test 3 and Test 5, the numbers the client saw in the editor.
        failedTests: [
          { index: 2, reason: 'Expected 4, got 5' },
          { index: 4, reason: 'Expected 3, got 4' },
        ],
      },
    ],
  },
  postMortem: {
    difficulty: 'Good',
    answers: [
      {
        id: 'pm-went-well',
        question: 'What went well?',
        answer:
          'Recognized the O(n²) DP quickly and remembered the tails-array trick from the sprint reading. Bisect usage felt natural this time.',
      },
      {
        id: 'pm-improvement',
        question: 'What could use improvement?',
        answer:
          'First submit failed on duplicates — I used bisect_right by habit. Spent ~6 minutes debugging before spotting it, which was really annoying. Is bisect_right ever actually the right call for this pattern?',
      },
    ],
    // Named from this card's own catalogue seed (`MOCK_CODING_APPROACHES`)
    // rather than invented: the client's answers describe the tails-array
    // trick, so the approach they report is the one the catalogue calls
    // "Patience sorting", and the alternative the section offers is the other
    // entry. A label naming no approach the problem has would demo a state
    // the live path cannot produce.
    approach: { source: 'catalog', label: 'Patience sorting' },
    lastEditedWhen: '2 days ago',
  },
  comments: [
    {
      id: 'coding-1',
      kind: 'praise',
      section: 'code',
      target: 'Code · Overall',
      source: 'ai',
      status: 'approved',
      body: 'O(n log n) reached on attempt two — most candidates never get past the O(n²) DP. The bisect usage is idiomatic.',
    },
    {
      id: 'coding-2',
      kind: 'suggestion',
      section: 'code',
      target: 'Code · L5–L8',
      source: 'ai',
      // 'approved', not 'pending' (LG-632): AI-draft surfaces are stripped
      // from coding reviews specifically, so the coding seeds carry no
      // pending drafts — approved is the seed state whose action (Mark
      // posted) works. The behavioral/system-design seeds keep theirs.
      status: 'approved',
      lines: [5, 8],
      diff: {
        before:
          'if i == len(tails):\n    tails.append(x)\nelse:\n    tails[i] = x',
        after: 'tails[i:i+1] = [x]',
      },
      body: 'The append/replace branch collapses into one slice assignment — same behavior, four lines shorter.',
    },
    {
      id: 'coding-3',
      kind: 'critique',
      section: 'postmortem',
      target: 'Post-mortem · Improvement',
      source: 'ai',
      // See coding-2's note — approved, since coding seeds carry no drafts.
      status: 'approved',
      body: 'You name the duplicate bug but not the invariant behind it — WHY does tails stay sorted? If you can’t articulate that, this won’t survive interviewer probing.',
    },
    {
      id: 'coding-4',
      kind: 'critique',
      section: 'pacing',
      target: 'Pacing · Total',
      source: 'ai',
      status: 'approved',
      // Grounded in the seed's own stored facts (LG-632): the pacing total
      // and the post-mortem's failed-first-submit and ~6-minute claims — no
      // benchmark bands, averages, or note, which the coding seed no longer
      // carries.
      body: '28:14 including a wrong submit — and your own post-mortem puts ~6 of those minutes on debugging the duplicates case. Cutting that guess-and-check loop is where the time comes back.',
    },
    // The former coding-5 (Verification · Runs) is gone with its section
    // (LG-632): unlike coding-1's re-anchor, its whole body restated the
    // removed fabricated run counts and targets, so there was nothing true
    // left to keep.
  ],
}

/**
 * A session nobody submitted (LG-646 PR 2) — the `Worked · not submitted`
 * row, here also unassigned practice: the client picked Two Sum themselves,
 * fought it for a while, and never handed anything in. One list per week, so
 * it sits beside the reviews with a badge rather than in a sub-list; no
 * review exists, so it carries no lifecycle, no comments, and its expanded
 * body is the read-only post-mortem.
 */
export const MOCK_PRACTICE_SESSION: PracticeSessionRow = {
  id: 'practice-two-sum',
  type: 'coding',
  title: 'Two Sum',
  // What the session can prove about itself: time, runs, submits, pass state.
  chips: ['41:20', '4 runs', '1 submit', 'Not passed'],
  submittedWhen: 'Worked 5 hours ago',
  workedAtMs: Date.parse('2026-08-12T07:00:00.000Z'),
  pacing: { total: '41:20', totalNote: '', companyRows: [] },
  postMortem: {
    difficulty: 'Hard',
    answers: [
      {
        id: 'q-struggle',
        question: 'Where did you get stuck?',
        answer:
          'Kept missing the complement lookup — brute forced it first, then ran out of steam converting to the hash-map pass.',
      },
    ],
    approach: null,
    lastEditedWhen: '4 hours ago',
  },
  comments: [],
  complexity: null,
  notSubmitted: true,
  unassigned: true,
  // They wrote it up, which is half of what qualifying means (spec D2), so
  // this is the row the *Pull into this week* action exists for (LG-647).
  pullable: true,
}

/**
 * Alternative-approach seeds for the LIS card (LG-640).
 *
 * These used to be a module constant *inside* the section component, which
 * meant every review of every problem was told LIS had three solutions. The
 * live path now reads `Approach` → `Solution` → `ApproachComplexity` for the
 * review's own problem (`listReviewApproaches`) and carries none of this; what
 * is left here is fixture data, seeding the mock shell the design walkthrough
 * runs on, in the same place LG-632 left the behavioral and system-design
 * seeds.
 *
 * The invented per-approach "note" prose is gone with the constant. The
 * catalog's own analogue is `Solution.explanation`, so the fixture carries
 * that field and leaves it null rather than restating editorial that no row
 * holds. The complexities are the textbook figures for these two algorithms,
 * with their explanation columns left null for the same reason.
 */
export const MOCK_CODING_APPROACHES: ReviewApproach[] = [
  {
    approachId: 'approach-lis-dp',
    name: 'O(n²) DP',
    language: 'python',
    code: 'def lengthOfLIS(nums):\n    dp = [1] * len(nums)\n    for i in range(len(nums)):\n        for j in range(i):\n            if nums[j] < nums[i]:\n                dp[i] = max(dp[i], dp[j] + 1)\n    return max(dp)',
    explanation: null,
    complexities: [
      {
        complexityId: 'complexity-lis-dp',
        functionName: 'length-of-lis',
        timeComplexity: 'O(n²)',
        spaceComplexity: 'O(n)',
        timeComplexityExplanation: null,
        spaceComplexityExplanation: null,
      },
    ],
  },
  {
    approachId: 'approach-lis-patience',
    name: 'Patience sorting',
    language: 'python',
    code: 'def lengthOfLIS(nums):\n    tails = []\n    for x in nums:\n        i = bisect_left(tails, x)\n        if i == len(tails):\n            tails.append(x)\n        else:\n            tails[i] = x\n    return len(tails)',
    explanation: null,
    complexities: [
      {
        complexityId: 'complexity-lis-patience',
        functionName: 'length-of-lis',
        timeComplexity: 'O(n log n)',
        spaceComplexity: 'O(n)',
        timeComplexityExplanation: null,
        spaceComplexityExplanation: null,
      },
    ],
  },
]

/**
 * The plan weeks the seeds were worked in (LG-646), newest first — the shape
 * the live spine has.
 *
 * Two of them, deliberately: the newest opens by default and the older one
 * stays folded, which is the behaviour that keeps a client's whole history off
 * the wire, and a single-week fixture could not show it.
 *
 * The counts are stated rather than derived from the rows. On the live path
 * they are server-computed over the whole week, including work the tab has not
 * loaded, so a fixture that recomputed them from its own rows would be
 * modelling something the real header cannot do.
 */
export const MOCK_REVIEW_WEEKS: ReviewPlanWeek[] = [
  {
    id: 'week-3',
    number: 3,
    startsOnKey: '2026-08-10',
    endsOnKey: '2026-08-16',
    status: 'published',
    awaitingCount: 2,
    // Three rows: the two reviews plus the practice session — worked counts
    // every row the week holds, whether or not anything was submitted.
    workedCount: 3,
  },
  {
    id: 'week-2',
    number: 2,
    startsOnKey: '2026-08-03',
    endsOnKey: '2026-08-09',
    status: 'closed',
    awaitingCount: 1,
    workedCount: 1,
  },
]
